import { useEffect, useState, useRef, useCallback } from "react";
import usePfqAxios from '../hooks/usePfqAxios';
import useUpdateStream from './useUpdateStream';
import Constants from '../Constants';
import AppConfig from "../AppConfig";

const replaceLastStreamingMessage = (prev, replacement) => {
    const updated = [...prev];
    const lastIdx = updated.length - 1;
    if (lastIdx >= 0 && updated[lastIdx]?.isStreaming) {
        updated[lastIdx] = replacement;
    }
    return updated;
};

const useChatSession = (caseId) => {
    const [chatMessages, setChatMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [streamRequest, setStreamRequest] = useState(null);
    const isStreamingRef = useRef(false);

    const { getData } = usePfqAxios({ timeout: 60000 });

    useEffect(() => {
        async function loadHistory() {
            try {
                setIsLoading(true);
                setError(null);
                const res = await getData(`${Constants.ApiUrl.chat.getMessages}?caseId=${caseId}`);
                setChatMessages(res?.data?.messages || []);
            } catch (err) {
                setError(err.message || "Failed to load chat history");
            } finally {
                setIsLoading(false);
            }
        }
        loadHistory();
    }, [caseId, getData]);

    const appendChunk = useCallback((text) => {
        setChatMessages((prev) => {
            const last = prev[prev.length - 1];
            if (!last?.isStreaming) return prev;
            const updated = [...prev];
            updated[updated.length - 1] = { ...last, message: last.message + (text || "") };
            return updated;
        });
    }, []);

    const finalizeMessage = useCallback((frame) => {
        isStreamingRef.current = false;
        setIsLoading(false);
        setStreamRequest(null);
        setChatMessages((prev) => replaceLastStreamingMessage(prev, frame.error
            ? { author: "assistant", message: frame.message || "Error generating response", isError: true }
            : { author: frame.author || "assistant", message: frame.message, exhibitSignedUrls: frame.exhibitSignedUrls }));
    }, []);

    const handleUpdate = useCallback((frame) => {
        if (frame.done) return finalizeMessage(frame);
        appendChunk(frame.text);
    }, [appendChunk, finalizeMessage]);

    useUpdateStream({ request: streamRequest, onUpdate: handleUpdate });

    const sendMessage = useCallback((chatInput, setOpen) => {
        if (setOpen) setOpen(false);
        if (!chatInput.trim() || isStreamingRef.current) return;

        const userMessage = { author: "user", message: chatInput };
        const streamingPlaceholder = { author: "assistant", message: "", isStreaming: true };
        setChatMessages((prev) => [...prev, userMessage, streamingPlaceholder]);
        setIsLoading(true);
        setError(null);
        isStreamingRef.current = true;

        // per-turn nonce (message count) so the url-keyed reader re-opens even for a repeated question; the endpoint ignores it
        setStreamRequest({
            url: `${AppConfig.PfqBaseUrl}${Constants.ApiUrl.chat.handleChat}?turn=${chatMessages.length}`,
            body: { caseId, query: chatInput, isExpedited: true }
        });
    }, [caseId, chatMessages.length]);

    return { chatMessages, sendMessage, isLoading, error };
};

export default useChatSession;
